'use client';

import type React from 'react';

export default function Page() {
  return (
    <>
      <section className="section-md bg-gradient-to-b from-primary/20 to-background/5">
        <div className="container container-max">
          {/* Hero Section */}
          <div className="space-y-4 text-center">
            <h1 className="mb-6 font-extrabold text-transparent hero-title bg-clip-text bg-gradient-to-l from-primary to-secondary ">
              Site Terms of Use
            </h1>

            <p className="max-w-3xl mx-auto text-lg text-foreground/70">
              Effective Date: January 2025
            </p>
          </div>
        </div>
      </section>
      <section className="section-md">
        <div className="container space-y-12 container-md">
          <p className="mb-4">
            Welcome to PayStop. By accessing or using our services, including our website and mobile
            applications (the "Platform"), you agree to comply with and be bound by these Terms and
            Conditions. Please read them carefully before using the Platform.
          </p>

          <p className="mb-4">
            These Terms and Conditions govern your use of PayStop services, including, but not
            limited to, accessing digital banking accounts, mobile wallets, traditional bank
            accounts, and other financial services through our network of agents.
          </p>

          <p className="mb-6">
            If you do not agree with these Terms and Conditions, you should immediately discontinue
            use of the Platform.
          </p>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">1. General Use of the Platform</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop offers digital financial services, including deposit, withdrawal, and
                transfer capabilities through our network of agents. By using the Platform, you
                agree that:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  You will comply with all applicable laws and regulations related to financial
                  transactions, data privacy, and security in your jurisdiction.
                </li>
                <li>
                  You are responsible for maintaining the confidentiality of your account
                  credentials and any personal information you share on the Platform.
                </li>
                <li>
                  You will not use the Platform for any unlawful or fraudulent activities, including
                  but not limited to money laundering, identity theft, or other illegal actions.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">2. Account Registration</h2>
            <ul className="pl-6 mb-2 list-disc">
              <li>
                <strong>Customers:</strong> You do not need to sign up for a PayStop account. You
                can access your digital bank account, mobile wallet, or traditional bank account
                through our agents who are connected to your account provider via a secured API.
              </li>
              <li>
                <strong>Agents:</strong> To become an agent, you must register with PayStop and fund
                your account through a commercial bank. You may also be required to submit valid
                documentation to verify your identity or business.
              </li>
              <li>
                <strong>Agent Admins:</strong> If you are an Agent Admin (appointed by a Super
                Agent), you will be granted access to manage transactions and the operation of the
                Super Agent's network. You must comply with all applicable rules and policies
                related to transaction management and security.
              </li>
            </ul>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">3. Use of the Services</h2>
            <div className="pl-4">
              <p className="mb-2">By using PayStop, you agree to the following terms:</p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  <strong>Financial Transactions:</strong> You may deposit, withdraw, and transfer
                  funds through PayStop agents. All transactions are processed through a secure
                  connection to your account provider, and PayStop will charge a service fee on
                  transactions.
                </li>
                <li>
                  <strong>Security:</strong> You agree to use strong, secure passwords and PINs for
                  accessing the Platform. You must promptly notify PayStop if you suspect any
                  unauthorised access to your account or any security breaches.
                </li>
                <li>
                  <strong>Agent Responsibilities:</strong> As an agent, you are responsible for
                  ensuring the proper conduct of transactions, verifying the identity of customers,
                  and ensuring that transactions comply with legal and regulatory requirements.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">4. Fees and Payments</h2>
            <ul className="pl-6 mb-2 list-disc">
              <li>
                <strong>Transaction Fees:</strong> PayStop charges a fee for transactions
                facilitated through its network of agents. Fees will be displayed within the
                Platform at the time of the transaction.
              </li>
              <li>
                <strong>Agent Fees:</strong> Agents will earn rewards for facilitating transactions.
                These rewards are based on the value of the transactions, with a percentage paid to
                agents for deposits, withdrawals, and transfers.
              </li>
              <li>
                <strong>Payment Method:</strong> Payments for services can be made via credit/debit
                card, bank transfer, or other methods as offered by PayStop.
              </li>
            </ul>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">5. Prohibited Activities</h2>
            <div className="pl-4">
              <p className="mb-2">You agree not to:</p>
              <ul className="pl-6 mb-2 list-disc">
                <li>
                  Use the Platform for any unlawful activities, including fraud, money laundering,
                  or the sale of illegal goods or services.
                </li>
                <li>
                  Attempt to reverse engineer, decompile, or hack the Platform or any part of it.
                </li>
                <li>
                  Use the Platform to interfere with, disrupt, or damage the integrity of the
                  services provided by PayStop.
                </li>
                <li>
                  Impersonate any person or entity, or falsely claim affiliation with any
                  individual, business, or organisation.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">6. Privacy and Data Protection</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop takes your privacy seriously. By using our services, you agree to the
                PayStop Privacy Policy, which outlines how we collect, use, and protect your
                personal information. This includes:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>Collecting personal details to verify identity and provide services.</li>
                <li>
                  Storing transaction histories and other relevant information as necessary for
                  service provision.
                </li>
                <li>
                  Sharing information only with trusted third-party partners who help us deliver our
                  services, subject to confidentiality agreements.
                </li>
              </ul>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">7. Intellectual Property</h2>
            <div className="pl-4">
              <p className="mb-2">
                All content, design, logos, trademarks, and other intellectual property found on the
                PayStop Platform are owned by PayStop or its licensors. You are granted a limited,
                non-exclusive, non-transferable license to access and use the Platform for personal
                and business use, as permitted by these Terms.
              </p>
              <p className="mb-2">
                You may not copy, reproduce, distribute, or create derivative works based on the
                Platform or any of its content without prior written permission from PayStop.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">8. Termination</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop reserves the right to suspend or terminate your access to the Platform at
                any time for any reason, including but not limited to:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>Violation of these Terms and Conditions.</li>
                <li>Fraudulent or unlawful activities.</li>
                <li>
                  Failure to comply with the platform's security or identity verification policies.
                </li>
              </ul>
              <p className="mb-2">
                If your account is suspended or terminated, you may no longer have access to any
                services provided through PayStop, and any pending transactions may be affected.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">9. Limitation of Liability</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop is not responsible for any loss, damage, or inconvenience caused by:
              </p>
              <ul className="pl-6 mb-2 list-disc">
                <li>Unauthorised access to your account.</li>
                <li>Errors or failures in the transaction process or network.</li>
                <li>
                  Delays or failures caused by third-party service providers, including mobile
                  wallets and digital banking services.
                </li>
              </ul>
              <p className="mb-2">
                Our liability is limited to the maximum extent permitted by law, and PayStop will
                not be liable for any indirect, incidental, or consequential damages.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">
              10. Dispute Resolution and Governing Law
            </h2>
            <div className="pl-4">
              <p className="mb-2">
                These Terms and Conditions are governed by the laws of [Insert
                Country/Jurisdiction]. Any disputes related to the use of the Platform shall be
                resolved through binding arbitration, and both parties agree to submit to the
                exclusive jurisdiction of the courts in [Insert Jurisdiction].
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">11. Changes to Terms and Conditions</h2>
            <div className="pl-4">
              <p className="mb-2">
                PayStop reserves the right to modify or update these Terms and Conditions at any
                time. Any changes will be posted on this page, and the updated Terms and Conditions
                will become effective once published. We encourage you to review this page
                periodically to stay informed about any changes.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <h2 className="mb-2 text-2xl font-semibold">12. Contact Information</h2>
            <div className="pl-4">
              <p className="mb-2">
                If you have any questions or concerns about these Terms and Conditions, please
                contact us at:
              </p>
              <p className="mb-2">Email: support@paystop.io</p>
              <p className="mb-8">Phone: +237 676962723</p>
              <i className="mb-2">
                By using PayStop, you acknowledge that you have read, understood, and agree to be
                bound by these Terms and Conditions. Thank you for using PayStop!
              </i>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
